import type {
  ExternalToolsPayload,
  ExternalToolsUI,
  McpServer,
  McpServerPayload,
  McpTool,
  McpToolPayload,
} from 'shared/src'
import { isNotBlank } from '@/utils/String'

const toolToPayload = (t: McpTool): McpToolPayload => {
  // ponytail: until we have a dedicated description-override UI, persist the discovered MCP
  // description in `description_override` so the tooltip survives a reload.
  const description = isNotBlank(t.description_override) ? t.description_override : t.description
  return {
    name: t.name,
    ...(t.enabled === false ? { enabled: false as const } : {}),
    ...(description ? { description_override: description } : {}),
    ...(t.category ? { category: t.category } : {}),
    ...(t.inputSchema ? { parameters: t.inputSchema } : {}),
  }
}

const serverToPayload = (s: McpServer): McpServerPayload => {
  const overrides = Object.fromEntries(
    Object.entries(s.config_overrides ?? {}).filter(([k]) => isNotBlank(k)),
  )

  return {
    label: s.label,
    url: s.url,
    type: 'mcp',
    headers: Object.fromEntries(
      s.headers.filter((h) => isNotBlank(h.key)).map((h) => [h.key, h.value]),
    ),
    ...(Object.keys(overrides).length ? { config_overrides: overrides } : {}),
    timeout_seconds: s.timeout_seconds,
    tools: s.tools.map(toolToPayload),
  }
}

export const serializeExternalTools = (ui: ExternalToolsUI): ExternalToolsPayload => ({
  max_tool_calls_per_turn: ui.maxToolCallsPerTurn,
  servers: ui.servers.map(serverToPayload),
})

const KNOWN_KEYS = new Set([
  'label',
  'url',
  'type',
  'headers',
  'config_overrides',
  'timeout_seconds',
  'tools',
])

const payloadToServer = (p: McpServerPayload): McpServer => ({
  label: p.label,
  url: p.url,
  type: 'mcp',
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  headers: Object.entries(p.headers ?? {}).map(([key, value]) => ({ key, value })),
  config_overrides: Object.fromEntries(
    Object.entries(p)
      .filter(([k]) => !KNOWN_KEYS.has(k))
      .map(([k, v]) => [k, String(v)]),
  ),
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  timeout_seconds: p.timeout_seconds ?? 10,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  tools: (p.tools ?? []).map((t) => ({
    name: t.name,
    enabled: t.enabled === false ? false : true,
    description_override: t.description_override,
    category: t.category,
    inputSchema: t.parameters,
  })),
})

export const deserializeExternalTools = (
  payload: ExternalToolsPayload | undefined,
): ExternalToolsUI => ({
  maxToolCallsPerTurn: payload?.max_tool_calls_per_turn ?? 5,
  servers: (payload?.servers ?? []).map(payloadToServer),
})
